import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Upload, Trash2, Loader2, FileText, Pencil, Sparkles, PartyPopper, Building2 } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { OnboardingProvider, useOnboarding } from "@/context/OnboardingContext";
import { Spinner } from "@/components/common/Common";
import { inr } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = [
  { slug: "profile", label: "Profile" }, { slug: "property", label: "Property" },
  { slug: "documents", label: "Documents" }, { slug: "requirements", label: "Requirements" },
  { slug: "review", label: "Review" },
];
const ALL = ["Account", ...STEPS.map((s) => s.label), "Project"];

export default function OnboardingFlow() {
  return <OnboardingProvider><Flow /></OnboardingProvider>;
}

function Flow() {
  const loc = useLocation();
  const slug = loc.pathname.split("/").pop();
  const { loaded } = useOnboarding();
  if (slug === "success") return <Success />;
  if (!loaded) return <div className="min-h-screen bg-sp-light"><Spinner label="Restoring your progress..." /></div>;
  return <StepShell slug={slug} />;
}

function StepShell({ slug }) {
  const nav = useNavigate();
  const { data, setField, setStep, createProject } = useOnboarding();
  const idx = STEPS.findIndex((s) => s.slug === slug);
  const step = STEPS[idx] || STEPS[0];
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setField(k, v, slug);
  const validate = () => {
    const e = {};
    if (slug === "profile") {
      if (!data.full_name?.trim()) e.full_name = "Please enter your full name";
      if (!/^[6-9]\d{9}$/.test((data.mobile || "").trim())) e.mobile = "Enter a valid 10-digit Indian mobile number";
      if (!data.city?.trim()) e.city = "City is required";
    }
    if (slug === "property") {
      if (!data.name?.trim()) e.name = "Give your project a name";
      if (!data.address?.trim()) e.address = "Site address is required";
      if (!/^\d{6}$/.test((data.pincode || "").trim())) e.pincode = "Enter a valid 6-digit pincode";
      if (!(Number(data.land_area) > 0)) e.land_area = "Enter a valid land area";
      if (!data.property_status) e.property_status = "Select property status";
    }
    if (slug === "requirements") {
      if (!(Number(data.builtup_area) > 0)) e.builtup_area = "Enter built-up area";
      if (!(Number(data.budget) > 0)) e.budget = "Enter a valid budget";
      if (!data.timeline) e.timeline = "Select a timeline";
      if (!data.current_stage) e.current_stage = "Select your current stage";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = async () => {
    if (!validate()) { toast.error("Please complete the highlighted fields"); return; }
    if (idx < STEPS.length - 1) { const n = STEPS[idx + 1].slug; setStep(n); nav(`/onboarding/${n}`); }
  };
  const back = () => { if (idx > 0) nav(`/onboarding/${STEPS[idx - 1].slug}`); else nav("/onboarding"); };

  const submit = async () => {
    if (!data._confirm) { toast.error("Please confirm your details are correct"); return; }
    setBusy(true);
    try { const p = await createProject(); nav("/onboarding/success"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not create project"); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-sp-light flex flex-col">
      <header className="bg-sp-navy text-white"><div className="sp-container py-4 flex items-center justify-between"><Logo dark /><span className="text-sm text-white/60">Step {idx + 2} of 6</span></div></header>

      {/* Progress */}
      <div className="bg-white border-b border-slate-200">
        <div className="sp-container py-4 flex items-center gap-2 overflow-x-auto sp-scroll">
          {ALL.map((label, i) => {
            const state = i === 0 ? "done" : i - 1 < idx ? "done" : i - 1 === idx ? "active" : "todo";
            return (
              <div key={label} className="flex items-center gap-2 shrink-0">
                <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-medium ${state === "done" ? "bg-sp-orange text-white" : state === "active" ? "bg-sp-navy text-white" : "bg-slate-200 text-slate-400"}`}>{state === "done" ? <Check className="h-3.5 w-3.5" /> : i + 1}</div>
                <span className={`text-xs ${state === "todo" ? "text-slate-400" : "text-sp-navy font-medium"} hidden sm:block`}>{label}</span>
                {i < ALL.length - 1 && <div className={`h-px w-6 ${i - 1 < idx ? "bg-sp-orange" : "bg-slate-200"}`} />}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex-1 sp-container max-w-2xl py-8 pb-28 w-full">
        <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-8" data-testid={`onboarding-${slug}`}>
          {slug === "profile" && <Profile data={data} set={set} errors={errors} />}
          {slug === "property" && <Property data={data} set={set} errors={errors} />}
          {slug === "documents" && <Documents />}
          {slug === "requirements" && <Requirements data={data} set={set} errors={errors} />}
          {slug === "review" && <Review data={data} set={set} nav={nav} />}
        </div>
      </div>

      {/* Sticky footer nav */}
      <div className="fixed bottom-0 inset-x-0 bg-white border-t border-slate-200 z-20">
        <div className="sp-container max-w-2xl py-3 flex items-center justify-between">
          <Button variant="ghost" onClick={back} data-testid="onb-back"><ArrowLeft className="h-4 w-4 mr-1" />Back</Button>
          {slug === "review" ? (
            <Button data-testid="onb-create" disabled={busy || !data._confirm} onClick={submit} className="bg-sp-orange hover:bg-sp-orangedark rounded-full px-7 h-11">
              {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</> : "Create My Project"}
            </Button>
          ) : (
            <Button data-testid="onb-continue" onClick={next} className="bg-sp-orange hover:bg-sp-orangedark rounded-full px-7 h-11">Continue<ArrowRight className="h-4 w-4 ml-1" /></Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Steps ----------
function Head({ title, sub }) { return <div className="mb-6"><h1 className="text-2xl font-bold text-sp-navy">{title}</h1><p className="text-sm text-slate-500 mt-1">{sub}</p></div>; }
function F({ label, error, children, full }) { return <div className={full ? "sm:col-span-2" : ""}><Label className="text-xs text-slate-500">{label}</Label><div className="mt-1.5">{children}</div>{error && <p className="text-xs text-red-600 mt-1">{error}</p>}</div>; }
function Sel({ value, onChange, opts, testid }) { return <Select value={value} onValueChange={onChange}><SelectTrigger data-testid={testid}><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent></Select>; }

function Profile({ data, set, errors }) {
  const { user } = useAuth();
  const { loaded } = useOnboarding();
  React.useEffect(() => {
    if (loaded && !data.full_name && user?.name) set("full_name", user.name);
    // eslint-disable-next-line
  }, [loaded, user]);
  return (
    <div>
      <Head title={`Tell us about yourself${user?.name ? `, ${user.name.split(" ")[0]}` : ""}.`} sub="We use this information to personalize your Sitepar experience." />
      <div className="grid sm:grid-cols-2 gap-4">
        <F label="Full name" error={errors.full_name} full><Input data-testid="prof-name" value={data.full_name || user?.name || ""} onChange={(e) => set("full_name", e.target.value)} placeholder="Rahul Sharma" /></F>
        <F label="Mobile number" error={errors.mobile}><Input data-testid="prof-mobile" value={data.mobile || ""} onChange={(e) => set("mobile", e.target.value)} placeholder="98765 43210" /></F>
        <F label="Email"><Input value={user?.email || ""} disabled /></F>
        <F label="Which city are you building in?" error={errors.city}><Input data-testid="prof-city" value={data.city || ""} onChange={(e) => set("city", e.target.value)} placeholder="Bengaluru" /></F>
        <F label="Preferred language"><Sel value={data.language || "English"} onChange={(v) => set("language", v)} opts={["English", "Hindi", "Kannada", "Tamil", "Telugu", "Malayalam", "Marathi"]} /></F>
      </div>
    </div>
  );
}

function Property({ data, set, errors }) {
  return (
    <div>
      <Head title="Tell us about your site." sub="This becomes the foundation of your project." />
      <div className="grid sm:grid-cols-2 gap-4">
        <F label="Property / project name" error={errors.name} full><Input data-testid="prop-name" value={data.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Sharma Residence" /></F>
        <F label="Site address" error={errors.address} full><Input data-testid="prop-address" value={data.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="123, 4th Cross, Whitefield" /></F>
        <F label="State"><Input value={data.state || ""} onChange={(e) => set("state", e.target.value)} placeholder="Karnataka" /></F>
        <F label="Pincode" error={errors.pincode}><Input data-testid="prop-pincode" value={data.pincode || ""} onChange={(e) => set("pincode", e.target.value)} placeholder="560066" /></F>
        <F label="How large is your site? (sq.ft)" error={errors.land_area}><Input data-testid="prop-land" type="number" value={data.land_area || ""} onChange={(e) => set("land_area", e.target.value)} placeholder="2400" /></F>
        <F label="Road facing"><Sel value={data.road_facing} onChange={(v) => set("road_facing", v)} opts={["North", "South", "East", "West", "North-East", "North-West", "South-East", "South-West"]} testid="prop-road" /></F>
        <F label="North (ft)"><Input value={data.north || ""} onChange={(e) => set("north", e.target.value)} /></F>
        <F label="South (ft)"><Input value={data.south || ""} onChange={(e) => set("south", e.target.value)} /></F>
        <F label="East (ft)"><Input value={data.east || ""} onChange={(e) => set("east", e.target.value)} /></F>
        <F label="West (ft)"><Input value={data.west || ""} onChange={(e) => set("west", e.target.value)} /></F>
        <F label="Property status" error={errors.property_status}><Sel value={data.property_status} onChange={(v) => set("property_status", v)} opts={["Own Land", "Purchased", "Under Purchase", "Family Property", "Other"]} testid="prop-status" /></F>
        <F label="Property value (₹, optional)"><Input type="number" value={data.property_value || ""} onChange={(e) => set("property_value", e.target.value)} /></F>
        <F label="Year of purchase (optional)"><Input value={data.year_of_purchase || ""} onChange={(e) => set("year_of_purchase", e.target.value)} /></F>
      </div>
    </div>
  );
}

const DOC_TYPES = ["Sale Deed", "Khata", "RTC", "Property Tax Receipt", "Survey Document", "Encumbrance Certificate", "Approved Plan", "Site Photos", "Other Documents"];
function Documents() {
  const { docs, uploadDoc, removeDoc } = useOnboarding();
  const [cat, setCat] = useState("Sale Deed");
  const [uploading, setUploading] = useState(false);
  const onFiles = async (files) => {
    for (const file of Array.from(files)) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!["pdf", "jpg", "jpeg", "png", "docx"].includes(ext)) { toast.error(`${file.name}: unsupported type`); continue; }
      if (file.size > 10 * 1024 * 1024) { toast.error(`${file.name}: exceeds 10 MB`); continue; }
      setUploading(true);
      try { await uploadDoc(file, cat); toast.success(`${file.name} uploaded`); }
      catch { toast.error(`${file.name}: upload failed`); }
      finally { setUploading(false); }
    }
  };
  return (
    <div>
      <Head title="Add your property documents." sub="Keep your important documents connected to your project. You can add more later." />
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Sel value={cat} onChange={setCat} opts={DOC_TYPES} testid="doc-type" />
      </div>
      <label onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}
        className="flex flex-col items-center justify-center border-2 border-dashed border-slate-200 rounded-xl py-10 cursor-pointer hover:border-sp-orange transition-colors" data-testid="doc-dropzone">
        {uploading ? <Loader2 className="h-7 w-7 text-sp-orange animate-spin" /> : <Upload className="h-7 w-7 text-slate-400" />}
        <span className="mt-2 text-sm text-slate-500">Drag & drop or click to upload ({cat})</span>
        <span className="text-xs text-slate-400 mt-0.5">PDF, JPG, PNG, DOCX · max 10 MB</span>
        <input type="file" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} data-testid="doc-input" />
      </label>
      {docs.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="doc-list">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2 min-w-0"><FileText className="h-4 w-4 text-sp-orange shrink-0" /><div className="min-w-0"><div className="text-sm text-sp-navy truncate">{d.name}</div><div className="text-xs text-slate-400">{d.category} · v{d.version}</div></div></div>
              <button onClick={() => removeDoc(d.id)} data-testid={`doc-del-${d.id}`}><Trash2 className="h-4 w-4 text-slate-300 hover:text-red-500" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Requirements({ data, set, errors }) {
  const started = data.current_stage === "Construction started";
  return (
    <div>
      <Head title="Tell us what you want to build." sub="We'll use this to create your initial project plan." />
      <div className="grid sm:grid-cols-2 gap-4">
        <F label="Built-up area (sq.ft)" error={errors.builtup_area}><Input data-testid="req-builtup" type="number" value={data.builtup_area || ""} onChange={(e) => set("builtup_area", e.target.value)} placeholder="2400" /></F>
        <F label="Number of floors"><Sel value={data.floors} onChange={(v) => set("floors", v)} opts={["Ground", "G+1", "G+2", "G+3", "Other"]} testid="req-floors" /></F>
        <F label="Bedrooms"><Input type="number" value={data.bedrooms || ""} onChange={(e) => set("bedrooms", e.target.value)} /></F>
        <F label="Bathrooms"><Input type="number" value={data.bathrooms || ""} onChange={(e) => set("bathrooms", e.target.value)} /></F>
        <F label="Parking"><Sel value={data.parking} onChange={(v) => set("parking", v)} opts={["0", "1", "2", "3+"]} /></F>
        <F label="Construction budget (₹)" error={errors.budget}><Input data-testid="req-budget" type="number" value={data.budget || ""} onChange={(e) => set("budget", e.target.value)} placeholder="7200000" /></F>
        <F label="Construction type"><Sel value={data.construction_type} onChange={(v) => set("construction_type", v)} opts={["Independent House", "Villa", "Duplex", "Other"]} testid="req-type" /></F>
        <F label="Design preference"><Sel value={data.design_preference} onChange={(v) => set("design_preference", v)} opts={["Modern", "Contemporary", "Traditional", "Minimal", "No Preference"]} /></F>
        <F label="Expected timeline" error={errors.timeline}><Sel value={data.timeline} onChange={(v) => set("timeline", v)} opts={["Less than 6 months", "6–9 months", "9–12 months", "12–18 months", "More than 18 months"]} testid="req-timeline" /></F>
        <F label="Current stage" error={errors.current_stage}><Sel value={data.current_stage} onChange={(v) => set("current_stage", v)} opts={["Land only", "Planning", "Design", "Approvals", "Contractor selected", "Construction started"]} testid="req-stage" /></F>
      </div>

      {started && (
        <div className="mt-4 grid sm:grid-cols-2 gap-4 rounded-lg bg-sp-light p-4">
          <F label="Existing contractor" full><Input value={data.existing_contractor || ""} onChange={(e) => set("existing_contractor", e.target.value)} placeholder="Contractor name" /></F>
          <F label="Approximate progress (%)"><Input type="number" value={data.approx_progress || ""} onChange={(e) => set("approx_progress", e.target.value)} /></F>
        </div>
      )}
      {data.design_preference === "No Preference" && (
        <div className="mt-4 rounded-lg border border-sp-orange/30 bg-sp-orange/5 p-4 text-sm text-sp-navy flex items-center gap-2"><Sparkles className="h-4 w-4 text-sp-orange" />No design yet? Explore Sitepar Studio after setup to generate floor plans & 3D.</div>
      )}

      <div className="mt-4"><F label="Additional requirements (optional)" full><Textarea data-testid="req-extra" value={data.additional_requirements || ""} onChange={(e) => set("additional_requirements", e.target.value)} placeholder="Pool, home office, courtyard, rental floor, senior-friendly design, Vastu, smart home..." /></F></div>
      <label className="mt-3 flex items-center gap-2 text-sm text-slate-600"><Checkbox checked={!!data.needs_contractor} onCheckedChange={(v) => set("needs_contractor", !!v)} data-testid="req-needs-contractor" />I need help finding a contractor</label>
    </div>
  );
}

function Review({ data, set, nav }) {
  const { docs } = useOnboarding();
  const secs = [
    ["Personal", "profile", [["Name", data.full_name], ["Mobile", data.mobile], ["City", data.city], ["Language", data.language]]],
    ["Property", "property", [["Project name", data.name], ["Address", data.address], ["Pincode", data.pincode], ["Land area", data.land_area && `${data.land_area} sq.ft`], ["Road facing", data.road_facing], ["Status", data.property_status]]],
    ["Construction", "requirements", [["Built-up", data.builtup_area && `${data.builtup_area} sq.ft`], ["Floors", data.floors], ["Bedrooms", data.bedrooms], ["Bathrooms", data.bathrooms], ["Budget", data.budget && inr(Number(data.budget))], ["Timeline", data.timeline], ["Stage", data.current_stage]]],
  ];
  return (
    <div>
      <Head title="Review your project." sub="Please confirm your details before we create your project." />
      <div className="space-y-4">
        {secs.map(([title, slug, rows]) => (
          <div key={title} className="rounded-lg border border-slate-200 p-4">
            <div className="flex items-center justify-between mb-3"><span className="font-semibold text-sp-navy text-sm">{title}</span><button onClick={() => nav(`/onboarding/${slug}`)} data-testid={`review-edit-${slug}`} className="text-xs text-sp-orange flex items-center gap-1"><Pencil className="h-3 w-3" />Edit</button></div>
            <div className="grid grid-cols-2 gap-y-2 text-sm">{rows.map(([l, v]) => <React.Fragment key={l}><span className="text-slate-400">{l}</span><span className="text-sp-navy text-right">{v || "—"}</span></React.Fragment>)}</div>
          </div>
        ))}
        <div className="rounded-lg border border-slate-200 p-4 flex items-center justify-between"><span className="font-semibold text-sp-navy text-sm">Documents</span><span className="text-sm text-slate-500">{docs.length} file{docs.length !== 1 ? "s" : ""} uploaded</span></div>
      </div>
      <label className="mt-5 flex items-center gap-2 text-sm text-slate-700"><Checkbox checked={!!data._confirm} onCheckedChange={(v) => set("_confirm", !!v)} data-testid="review-confirm" />I confirm that the information provided is correct.</label>
    </div>
  );
}

function Success() {
  const nav = useNavigate();
  const { createdProject } = useOnboarding();
  const p = createdProject;
  if (!p) return <div className="min-h-screen bg-sp-light"><Spinner label="Finalizing your project..." /></div>;
  return (
    <div className="min-h-screen bg-sp-navy sp-blueprint flex items-center justify-center p-6 text-white">
      <div className="max-w-md w-full text-center animate-fade-up">
        <div className="h-16 w-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto animate-pulse-ring"><PartyPopper className="h-8 w-8 text-emerald-400" /></div>
        <div className="sp-eyebrow mt-6">Project Created Successfully</div>
        <h1 className="text-3xl font-bold mt-2">Your project is ready.</h1>
        <div className="mt-6 sp-glass rounded-xl p-5 text-left">
          <div className="text-lg font-bold">{p.name}</div>
          <div className="text-xs font-mono text-sp-orange mt-0.5">{p.id}</div>
          <div className="text-sm text-white/70 mt-2">{p.location} · {p.builtup_area} sq.ft · {p.floors}</div>
          <div className="text-sm text-white/70">{inr(p.budget)} estimated budget · {p.completeness}% complete</div>
        </div>
        <div className="mt-5 text-left text-sm text-white/75 space-y-1.5">
          <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Your next steps</div>
          {["Review your construction plan", "Explore design options in Studio", "Review estimated costs", "Upload remaining documents", "Add your construction team"].map((s) => <div key={s} className="flex items-center gap-2"><Check className="h-4 w-4 text-sp-orange" />{s}</div>)}
        </div>
        <Button data-testid="go-to-project" onClick={() => nav(`/app/project/${p.id}`)} className="mt-6 w-full h-12 bg-sp-orange hover:bg-sp-orangedark rounded-full text-base">Go to My Project<ArrowRight className="h-4 w-4 ml-1" /></Button>
        <Button variant="ghost" onClick={() => nav(`/app/project/${p.id}?tab=ai`)} className="mt-2 w-full text-white/70 hover:text-white hover:bg-white/10">Explore Sitepar AI</Button>
      </div>
    </div>
  );
}
